import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "pc_install_banner_dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const useInstall = () => {
  const [prompt, setPrompt] = useState(window.__pcInstallPrompt || null);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const ready = () => setPrompt(window.__pcInstallPrompt);
    const done = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("pc-install-ready", ready);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("pc-install-ready", ready); window.removeEventListener("appinstalled", done); };
  }, []);
  const install = async () => {
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      window.__pcInstallPrompt = null;
      setPrompt(null);
      if (outcome === "accepted") toast.success("PostalCloud is being added to your device");
      return;
    }
    if (isIOS()) toast.info("On iPhone/iPad: tap the Share icon in Safari, then 'Add to Home Screen'.", { duration: 8000 });
    else toast.info("Open your browser menu (⋮) and choose 'Install app' or 'Add to Home screen'.", { duration: 8000 });
  };
  return { installed, install };
};

export const InstallAppButton = ({ testId = "install-app-btn", className = "" }) => {
  const { installed, install } = useInstall();
  if (installed) return null;
  return (
    <button data-testid={testId} onClick={install}
      className={`inline-flex items-center justify-center gap-2 h-9 px-4 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors ${className}`}>
      <Download size={14} /> Download app
    </button>
  );
};

export const InstallBanner = () => {
  const { installed, install } = useInstall();
  const [hidden, setHidden] = useState(localStorage.getItem(DISMISS_KEY) === "1");
  if (installed || hidden) return null;
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setHidden(true); };
  return (
    <div data-testid="install-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 p-4 sm:p-5">
      <img src="/brand/icon-192.png" alt="PostalCloud" className="w-12 h-12 rounded-xl shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Get the PostalCloud app</p>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">Add PostalCloud to your phone or computer for one-tap access to your verified mail. No app store needed.
          {isIOS() && <span className="inline-flex items-center gap-1"> On iPhone, tap <Share size={12} /> then "Add to Home Screen".</span>}</p>
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="install-banner-btn" onClick={install} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors"><Download size={14} /> Install</button>
        <button data-testid="install-banner-dismiss" onClick={dismiss} aria-label="Dismiss" className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors"><X size={16} /></button>
      </div>
    </div>
  );
};
